import React, { Component } from 'react';
import axios from 'axios';
import { passCsrfToken } from '../../helpers';
import { EmptyContent, Badge } from '../shared';
import { getMyGroupTripInterests, getInterestPaymentInfo, initiateRestPayment, updateNumberOfPeople } from '../../api/groupTourismApi';
import { BASE_URL } from '../../constants';
import { connect } from 'react-redux';
import { PaymentMethodModal } from '../payments';
import { formatMoneyLegacy } from '../../utils/currency';
import swal from 'sweetalert';

const STATUS_LABEL = {
  pending_payment: 'Awaiting interest',
  paid: 'Interest paid',
  rest_paid: 'Rest paid',
  removed: 'Removed'
};

const BADGE_TYPE = {
  pending_payment: 'warning',
  paid: 'info',
  rest_paid: 'success',
  removed: 'secondary'
};

class GroupTripBookings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      interests: [],
      loading: true,
      payingInterest: null,
      payingRest: null,
      payingAdditional: null,
      fetchError: null,
      showPaymentModal: false,
      paymentModalData: null
    };
  }

  componentDidMount() {
    passCsrfToken(document, axios);
    this.fetch();
    // Use visibility API instead of focus event to avoid triggering on button clicks
    // This only refreshes when user switches back to the tab
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    // Only fetch when tab becomes visible (user switched back to this tab)
    if (!document.hidden) {
      this.fetch();
    }
  }

  fetch = () => {
    this.setState({ loading: true, fetchError: null });
    getMyGroupTripInterests()
      .then((r) => {
        const raw = r.data?.group_tourism_interests;
        const list = Array.isArray(raw) ? raw : [];
        this.setState({ interests: list, loading: false, fetchError: null });
      })
      .catch((err) => {
        const errMsg = err.response?.status === 401 ? null : (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(' ') : (err.response?.data?.errors || 'Could not load your group trips.'));
        this.setState({ interests: [], loading: false, fetchError: errMsg });
        if (err.response?.status === 401) {
          swal('Sign in required', 'Please sign in to view your group trips.', 'info');
        }
      });
  };

  handlePayInterest = (interest) => {
    if (this.state.payingInterest || this.state.payingRest) return;
    
    this.setState({ payingInterest: interest.idx });
    getInterestPaymentInfo(interest.idx)
      .then((r) => {
        const d = r.data || {};
        if (!d.can_pay) {
          const msg = d.reason === 'slot_full'
            ? 'This tour is full. Your payment cannot be applied.'
            : d.reason === 'registration_closed_by_cutoff'
              ? 'Registration has closed (within the cutoff period before the trip). Interest payment is no longer available.'
              : 'Interest payment is not available for this registration.';
          swal('Cannot pay', msg, 'warning');
          this.setState({ payingInterest: null });
          return;
        }
        
        // Show payment method modal
        this.setState({
          showPaymentModal: true,
          paymentModalData: {
            productIdentity: interest.idx,
            amount: Number(d.amount || 0),
            productName: 'GROUP_TOURISM',
            productUrl: `${BASE_URL || ''}/bookings`,
            currencyCode: interest.group_tour?.currency_code || 'NPR'
          }
        });
      })
      .catch((e) => {
        swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not load payment info.', 'error');
        this.setState({ payingInterest: null });
      });
  };

  handlePayRest = (interest) => {
    if (this.state.payingInterest || this.state.payingRest || this.state.payingAdditional) return;
    if (!interest.can_pay_rest) return;
    
    this.setState({ payingRest: interest.idx });
    initiateRestPayment(interest.idx)
      .then((r) => {
        if (r.data?.idx != null && r.data?.amount != null) {
          // Show payment method modal
          this.setState({
            showPaymentModal: true,
            paymentModalData: {
              productIdentity: r.data.idx,
              amount: Number(r.data.amount),
              productName: 'GROUP_TOURISM_REST',
              productUrl: `${BASE_URL || ''}/bookings`,
              currencyCode: interest.group_tour?.currency_code || r.data.currency_code || 'NPR'
            }
          });
        } else {
          swal('Error', 'Could not start payment.', 'error');
          this.setState({ payingRest: null });
        }
      })
      .catch((e) => {
        swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not start payment.', 'error');
        this.setState({ payingRest: null });
      });
  };

  handleAddMoreSeats = (i) => {
    if (this.state.payingInterest || this.state.payingRest || this.state.payingAdditional) return;
    const gt = i.group_tour || {};
    const current = i.number_of_people || 1;
    const paid = gt.paid_people_count || 0;
    const max = gt.max_group_size;
    const maxTotal = (max != null) ? Math.max(0, (max - paid) + current) : 999;
    const maxClamped = maxTotal < current ? current : maxTotal;
    if (maxClamped <= current) {
      swal('Cannot add', 'No more spots available for this tour.', 'info');
      return;
    }
    const v = window.prompt(`You have ${current} seat(s). Enter new total to add more (max ${maxClamped}). You will pay interest for the extra seat(s).`, String(current + 1));
    if (v == null || v === '') return;
    const n = parseInt(v, 10);
    if (isNaN(n) || n < 1) {
      swal('Invalid', 'Please enter a valid number.', 'warning');
      return;
    }
    if (n <= current) {
      swal('Invalid', 'Enter a number greater than your current ' + current + ' to add more seats.', 'warning');
      return;
    }
    if (n > maxClamped) {
      swal('Invalid', `Maximum ${maxClamped} seat(s) allowed for this tour.`, 'warning');
      return;
    }
    this.setState({ payingAdditional: i.idx });
    updateNumberOfPeople(i.idx, n)
      .then((r) => {
        const d = r.data || {};
        if (d.idx != null && d.amount != null) {
          this.setState({
            showPaymentModal: true,
            paymentModalData: {
              productIdentity: d.idx,
              productName: 'GROUP_TOURISM_ADDITIONAL_INTEREST',
              productUrl: `${BASE_URL || ''}/bookings`,
              amount: Number(d.amount),
              currencyCode: (i.group_tour && i.group_tour.currency_code) || 'NPR',
            },
            amount: Number(d.amount),
          });
        }
        this.fetch();
      })
      .catch((e) => {
        swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not add seats.', 'error');
      })
      .finally(() => this.setState({ payingAdditional: null }));
  };

  handleClosePaymentModal = () => {
    this.setState({ 
      showPaymentModal: false, 
      paymentModalData: null,
      payingInterest: null,
      payingRest: null
    });
  };

  handlePaymentInitiated = (method) => {
    // Payment initiated - modal will close automatically
    this.setState({ 
      payingInterest: null,
      payingRest: null
    });
  };

  render() {
    const { interests, loading, payingInterest, payingRest, payingAdditional, fetchError, showPaymentModal, paymentModalData } = this.state;

    if (loading) {
      return <p className="text-muted">Loading…</p>;
    }

    if (fetchError) {
      return <div className="alert alert-danger">{fetchError}</div>;
    }

    const list = Array.isArray(interests) ? interests : [];
    if (list.length === 0) {
      return <EmptyContent>No group trip registrations yet.</EmptyContent>;
    }

    return (
      <>
        <div className="">
          {list.map((i) => {
          if (!i || !i.idx) return null;
          const gt = i.group_tour || {};
          const cc = gt.currency_code || 'NPR';
          const statusLabel = STATUS_LABEL[i.status] || i.status;
          const badgeType = BADGE_TYPE[i.status] || 'light';
          const people = i.number_of_people || 1;
          const interestAmount = (i.amount != null && i.amount > 0) ? i.amount : ((gt.interest_amount || 0) * people);
          const current = people;
          const paid = gt.paid_people_count || 0;
          const max = gt.max_group_size;
          const maxTotal = (max != null) ? Math.max(0, (max - paid) + current) : 999;
          const maxClamped = maxTotal < current ? current : maxTotal;

          return (
            <div key={i.idx} className="booking-card">
              <div className="booking-card__header">
                <h3 className="booking-card__title">{gt.name || 'Group trip'}</h3>
                <div className="booking-card__meta">
                  {gt.dates_display && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-calendar-alt" />
                      <span>{gt.dates_display}</span>
                    </div>
                  )}
                  {gt.from_to_display && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-route" />
                      <span>{gt.from_to_display}</span>
                    </div>
                  )}
                  {people > 0 && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-users" />
                      <span>{people} {people === 1 ? 'person' : 'people'}</span>
                    </div>
                  )}
                  {gt.spots_left != null && i.status === 'pending_payment' && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-info-circle" />
                      <span>Spots left: {gt.spots_left}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="booking-card__footer">
                <div className="booking-card__status">
                  <span className={`booking-card__badge booking-card__badge--${badgeType}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="booking-card__actions">
                  {i.status === 'pending_payment' && !i.can_pay_interest && (
                    <p className="booking-card__payment-disabled mb-2 mb-md-0 mr-md-2 text-muted" style={{ fontSize: '0.875rem' }}>
                      <i className="fas fa-info-circle" aria-hidden="true" /> Registration is closed for this trip; interest payment is no longer available.
                    </p>
                  )}
                  {i.status === 'paid' && (Number(i.rest_amount || 0) > 0) && !i.can_pay_rest && (
                    <p className="booking-card__payment-disabled mb-2 mb-md-0 mr-md-2 text-muted" style={{ fontSize: '0.875rem' }}>
                      <i className="fas fa-info-circle" aria-hidden="true" />
                      {(i.group_tour && i.group_tour.group_formed === false)
                        ? " Pay rest will be available once the trip is confirmed. You'll receive an email from us."
                        : ' Registration is closed for this trip; rest payment is no longer available.'}
                    </p>
                  )}
                  {i.can_pay_interest && (
                    <button
                      type="button"
                      className="booking-card__action-btn booking-card__action-btn--success"
                      onClick={() => this.handlePayInterest(i)}
                      disabled={!!payingInterest || !!payingRest || !!payingAdditional}
                    >
                      <i className="fas fa-credit-card" />
                      {payingInterest === i.idx ? 'Opening payment…' : `Pay Interest (${formatMoneyLegacy(interestAmount, cc)})`}
                    </button>
                  )}
                  {i.can_pay_rest && (
                    <button
                      type="button"
                      className="booking-card__action-btn booking-card__action-btn--primary"
                      onClick={() => this.handlePayRest(i)}
                      disabled={!!payingInterest || !!payingRest || !!payingAdditional}
                    >
                      <i className="fas fa-money-bill-wave" />
                      {payingRest === i.idx ? 'Opening payment…' : `Pay Rest (${formatMoneyLegacy(i.rest_amount || 0, cc)})`}
                    </button>
                  )}
                  {i.status === 'paid' && (
                    <p className="booking-card__tip text-muted small mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                      Need more seats? You can book again from the Group Trips page.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </div>
        
        <PaymentMethodModal
          open={showPaymentModal}
          onClose={this.handleClosePaymentModal}
          paymentParams={paymentModalData}
          amount={paymentModalData?.amount}
          onPaymentInitiate={this.handlePaymentInitiated}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.userStore?.currentUser || {}
});

export default connect(mapStateToProps)(GroupTripBookings);
